import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    SoundType,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../../AnimationContents';
import { GameLogic } from '../../../Logic/GameLogic';
import { SpineAnimationBaseController } from '../../Spine/SpineAnimationBaseController';
import { Graphics } from 'pixi.js';
import { RescueGameUIController } from './RescueGameUIController';
import { RescueModel } from '../../../Models/MiniGames/RescueModel';
import { DialogTextEnum, DialogTypeTextEnum } from '../../../Models/GameModel';
import { SoundConstants, SoundLevels } from '../../../Constants/SoundConstants';
import { RescueLogic } from '../../../Logic/MiniGames/RescueLogic';
import { testMobile } from '../../../Utils/TestMobile';

export interface IRescueGameControllerOptions {
    onComplete: () => void;
}

export class RescueGameController extends AnimationBaseController {
    public $model: RescueModel;

    private _width: number = 0;
    private _height: number = 0;

    private _backgroundShape: Graphics;
    private cross_in: SpineAnimationBaseController;
    private cross_out: SpineAnimationBaseController;
    private end_game: SpineAnimationBaseController;
    private game_session: SpineAnimationBaseController;
    private start_game: SpineAnimationBaseController;
    private game_ui: RescueGameUIController;

    public constructor(@Inject private _options: IRescueGameControllerOptions,
        @Inject private _rescueLogic: RescueLogic,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('RescueGameController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        const returning = <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.RESCUE_GAME_SCREEN_JSON,
            libraryName: 'mini_game_rescue',
            sounds: [
                {
                    id: SoundConstants.ICEBREAKER,
                    type: SoundType.SFX,
                },
            ],
            childControllers: {
                end_game: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        index: 3,
                        key: `end_game`,
                        url: `/images/spine/mini_game_rescue/mini_game_rescue.json`,
                        animations: [
                            'end_game',
                        ],
                    },
                },
                game_session: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        index: 3,
                        key: `game_session`,
                        url: `/images/spine/mini_game_rescue/mini_game_rescue.json`,
                        animations: [
                            'game_session',
                        ],
                    },
                },
                start_game: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        index: 3,
                        key: `start_game`,
                        url: `/images/spine/mini_game_rescue/mini_game_rescue.json`,
                        animations: [
                            'start_game',
                        ],
                    },
                },
                cross_in: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        index: 3,
                        key: `cross_in`,
                        url: `/images/spine/mini_game_rescue/mini_game_rescue.json`,
                        animations: [
                            'cross_in',
                        ],
                    },
                },
                cross_out: {
                    controller: SpineAnimationBaseController,
                    model: this.$model,
                    options: {
                        index: 3,
                        key: `cross_out`,
                        url: `/images/spine/mini_game_rescue/mini_game_rescue.json`,
                        animations: [
                            'cross_out',
                        ],
                    },
                },
                game_ui: {
                    model: this.$model,
                    instanceName: 'mini_game_rescue',
                    controller: RescueGameUIController,
                    options: {
                        index: 1,
                    },
                },
            },
            modelWatch: {
                running: (value: boolean) => {
                    if (this.game_ui.visible) {
                        if (!value && this._rescueLogic.isShowHintAllowed()) {
                            this.game_ui.visible = false;
                            this.game_session.changeAnimationSpeed(0);

                            if (this._rescueLogic.sliderValue() < 0.1) {
                                this._gameLogic.showDialogScreen(DialogTextEnum.rescue, DialogTypeTextEnum.hint, 0, () => this.game_ui.visible = true);
                            } else if (this._rescueLogic.sliderValue() > 1.9) {
                                this._gameLogic.showDialogScreen(DialogTextEnum.rescue, DialogTypeTextEnum.hint, 1, () => this.game_ui.visible = true);
                            }

                        } else {
                            this.game_session.changeAnimationSpeed(0.3);
                        }
                    }
                },
                sliderValue: (value: number) => {
                    if (this._rescueLogic.running()) {
                        if (value < 0.1) {
                            this._rescueLogic.stop();
                        } else if (value > 1.9) {
                            this._rescueLogic.stop();
                            this._rescueLogic.reset()
                        }
                    }
                },
            },

        };
        if (testMobile())
            returning.onPointerDown = ()=>{this._rescueLogic.setForward(true);};
            returning.onPointerUp = ()=>{this._rescueLogic.setForward(false);};

        return returning;
    }

    protected onInitialize(): void {
        this.cross_in = this.$childControllers.get('cross_in');
        this.start_game = this.$childControllers.get('start_game');
        this.game_session = this.$childControllers.get('game_session');
        this.end_game = this.$childControllers.get('end_game');
        this.cross_out = this.$childControllers.get('cross_out');
        this.game_ui = this.$childControllers.get('game_ui');

        this.cross_in.setAnimation('cross_in', false);
        this.start_game.setAnimation('start_game', false);

        this.game_ui.visible = false;

        this.cross_in.getSpine().state.onComplete = () => {
            this.cross_in.getSpine().state.onComplete = null;
            this.cross_in.resetAnimation();

            this._gameLogic.showDialogScreen(DialogTextEnum.rescue, DialogTypeTextEnum.start, 0, () => {
                this.start_game.resetAnimation();
                this.game_ui.visible = true;
                this._rescueLogic.init();

                this.soundManager.play(SoundConstants.ICEBREAKER, true, SoundLevels.MINIGAME_ACTION);

                this.game_session.setAnimation('game_session', false);
                this.game_session.getSpine().state.onComplete = () => {
                    this.game_ui.visible = false;
                    this._rescueLogic.stop();

                    this.soundManager.stop(SoundConstants.ICEBREAKER);

                    this._gameLogic.showDialogScreen(DialogTextEnum.rescue, DialogTypeTextEnum.end, 0, () => {
                        this.game_session.getSpine().state.onComplete = null;
                        this.game_session.resetAnimation();

                        this.cross_out.setAnimation('cross_out', false);
                        this.end_game.setAnimation('end_game', false);
                        this.end_game.getSpine().state.onComplete = () => {
                            this.end_game.getSpine().state.onComplete = null;

                            setTimeout(() => {
                                this._options.onComplete();
                            }, 500);
                        };
                    });
                };

                this._rescueLogic.run();
            });
        };

    }

    protected onResize(width: number, height: number, scale: number) {
        const scaleValue = Math.min(
            width / scale / 1200,
            height / scale / 1200,
        );

        this.scaleX = scaleValue;
        this.scaleY = scaleValue;

        if (this._backgroundShape) {
            const w = width * 10;
            const h = height * 10;

            this._backgroundShape.clear();
            this._backgroundShape.beginFill(0x3A6473);
            this._backgroundShape.drawRect(-w / 2, -h / 2, w, h);
            this._backgroundShape.endFill();
        }
    }

    protected onUpdate(frameRate: number) {
        if (this._rescueLogic.running())
            this._rescueLogic.increaseHardness();
        if (this._rescueLogic.running() && !this.$model.isForward) {
            this._rescueLogic.reducingAutomatickPosition(frameRate);

        }
        if (this.$model.isForward) {
            this._rescueLogic.forward(frameRate);
        }
    }
}
